import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import PnlGrid from "../../components/planning/pnl/PnlGrid";
import ConsolidatedPnlDrilldown from "../../components/group/ConsolidatedPnlDrilldown";
import { MONTHS, fmtSEK } from "../../components/planning/revenue/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const SECTION_LABELS = {
  total_sales: "Total Sales",
  total_direct: "Total Direct Costs",
  group_overhead: "Group Overhead (6591)",
  gross_profit: "Gross Profit",
  total_indirect: "Total Indirect Costs",
  pbt: "Profit Before Tax",
};
const ELIM_LABELS = {
  ic_revenue_elim: "Intercompany revenue eliminations",
  ic_cost_elim: "Intercompany cost eliminations",
  oh_recharge_elim: "Group Overhead recharge eliminations",
  oh_cost_elim: "Group Overhead 6591 eliminations",
  net_eliminations: "Unnamed consolidation residual (must be 0)",
  consolidation_only_adjustments: "Consolidation-only adjustments",
  ic_revenue_booked_elim: "Booked IC revenue eliminations (accounting actual)",
  ic_cost_booked_elim: "Booked IC cost eliminations (accounting actual)",
  ic_booked_difference: "IC Booked Difference (named consolidation-only adjustment)",
};

function StateBadge({ state, testId }) {
  if (state === "ACTUAL") {
    return <span className="inline-flex items-center rounded bg-[#0052CC] text-white text-[9px] font-bold px-1.5 py-px" data-testid={testId}>ACT</span>;
  }
  return <span className="inline-flex items-center rounded bg-slate-200 text-slate-600 text-[9px] font-bold px-1.5 py-px" data-testid={testId}>FCST</span>;
}

// Build 9A — Group → Consolidated P&L (read-only): canonical legal-entity P&Ls
// first, explicit intercompany eliminations second, external customer revenue
// always remains. Corrections happen at origin — never on this page.
export default function ConsolidatedPnlPage() {
  const { year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noGroup, setNoGroup] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const [bridgeMonth, setBridgeMonth] = useState(0); // 0 = full year

  useEffect(() => {
    api.get("/companies")
      .then(async (c) => {
        const hub = c.data.find((x) => x.is_group_company);
        if (!hub) { setNoGroup(true); return; }
        const yrs = await api.get(`/companies/${hub.id}/periods/years`);
        setYears(yrs.data);
        const pick = pickActiveYear(yrs.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!year) return;
    setLoading(true);
    api.get("/group/consolidated-pnl", { params: { year } })
      .then((res) => setData(res.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [year]);

  const bridge = bridgeMonth === 0 ? data?.reconciliation_fy : data?.months?.[String(bridgeMonth)]?.reconciliation;

  return (
    <div data-testid="consolidated-pnl-page">
      <PageHeader
        title="Consolidated P&L"
        subtitle="Legal entities calculate first — explicit intercompany eliminations second. External customer revenue always remains. Read-only: correct numbers at origin."
        actions={
          years.length > 0 && (
            <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="cpnl-year-select">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)} data-testid={`cpnl-year-${y}`}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
      />

      {noGroup && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="cpnl-no-group">
          <EmptyState title="No Group company configured" description="Mark a company as the Group company under Administration → Companies." testId="cpnl-no-group-state" />
        </div>
      )}

      {data?.warnings?.length > 0 && (
        <div className="mb-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-4" data-testid="cpnl-warnings">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#92400E] mb-1">
            <AlertTriangle className="h-4 w-4" /> Consolidation warnings
          </div>
          <ul className="text-xs text-[#92400E] space-y-0.5 list-disc pl-5">
            {data.warnings.map((w, i) => <li key={i} data-testid={`cpnl-warning-${i}`}>{w}</li>)}
          </ul>
        </div>
      )}

      {loading && (
        <div className="py-16 flex justify-center" data-testid="cpnl-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      )}

      {year && !loading && data && (
        <>
          <PnlGrid data={data} loading={false} onDrilldown={(line, month) => setDrilldown({ line, month })} />

          <div className="mt-6 bg-white border border-slate-200 rounded-lg p-5" data-testid="cpnl-reconciliation">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Reconciliation — Sum of legal entities → Consolidated Group</h2>
                <p className="text-xs text-slate-500 mt-0.5">Every elimination traces to an explicit linked intercompany transaction or a booked IC leg from the company invoicing model. Generated eliminations are zero-net; booked-leg mismatches surface as the named IC Booked Difference for Finance review — unnamed residuals must be zero.</p>
              </div>
              <div className="flex flex-wrap gap-1" data-testid="cpnl-bridge-months">
                <button
                  onClick={() => setBridgeMonth(0)}
                  className={`px-2 py-1 text-xs rounded-md border transition-colors ${bridgeMonth === 0 ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                  data-testid="cpnl-bridge-month-0"
                >
                  FY
                </button>
                {MONTHS.map((label, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setBridgeMonth(i + 1)}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${bridgeMonth === i + 1 ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                    data-testid={`cpnl-bridge-month-${i + 1}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {bridge && (
              <table className="w-full" data-testid="cpnl-bridge-table">
                <thead>
                  <tr className="bg-secondary/50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Section</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Sum legal entities</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Eliminations</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Consolidated</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(SECTION_LABELS).map(([key, label]) => {
                    const r = bridge[key] || {};
                    const elims = Object.entries(r).filter(([k]) => !["sum_legal", "consolidated"].includes(k));
                    return (
                      <tr key={key} className="border-b border-slate-100" data-testid={`cpnl-bridge-${key}`}>
                        <td className="py-2 px-3 text-sm font-semibold text-slate-800">
                          {label}
                          {elims.map(([k, v]) => (
                            <span key={k} className="block text-[11px] font-normal text-slate-500">
                              {ELIM_LABELS[k] || k}: {v === 0 ? "0" : fmtSEK(v)}
                            </span>
                          ))}
                        </td>
                        <td className="py-2 px-3 text-right text-sm text-slate-700">{fmtSEK(r.sum_legal)}</td>
                        <td className="py-2 px-3 text-right text-sm text-slate-700" data-testid={`cpnl-bridge-${key}-elim`}>
                          {fmtSEK(elims.reduce((s, [, v]) => s + (v || 0), 0))}
                        </td>
                        <td className="py-2 px-3 text-right text-sm font-bold text-slate-900" data-testid={`cpnl-bridge-${key}-consol`}>{fmtSEK(r.consolidated)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="mt-6 bg-white border border-slate-200 rounded-lg p-5" data-testid="cpnl-per-company">
            <h2 className="text-base font-semibold text-slate-800 mb-3">
              Legal entity contributions — {bridgeMonth === 0 ? "Full year" : MONTHS[bridgeMonth - 1]}
            </h2>
            <table className="w-full" data-testid="cpnl-company-table">
              <thead>
                <tr className="bg-secondary/50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Company</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">State</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Total Sales</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">PBT</th>
                </tr>
              </thead>
              <tbody>
                {data.per_company.map((c) => {
                  const cell = bridgeMonth === 0 ? c.fy : c.months[String(bridgeMonth)];
                  return (
                    <tr key={c.company_id} className="border-b border-slate-100" data-testid={`cpnl-company-${c.company_id}`}>
                      <td className="py-2 px-3 text-sm font-semibold text-slate-800">
                        {c.company_name}
                        {c.is_hub && <span className="ml-2 text-[10px] text-slate-400 font-normal">Group company</span>}
                      </td>
                      <td className="py-2 px-3">
                        {bridgeMonth === 0 ? <span className="text-xs text-slate-400">—</span> : (
                          <StateBadge state={cell.state} testId={`cpnl-company-state-${c.company_id}`} />
                        )}
                      </td>
                      <td className="py-2 px-3 text-right text-sm text-slate-700">{fmtSEK(cell.total_sales)}</td>
                      <td className="py-2 px-3 text-right text-sm text-slate-700">{fmtSEK(cell.pbt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-slate-500 mt-3" data-testid="cpnl-tax-note">{data.tax_note}</p>
          </div>
        </>
      )}

      <ConsolidatedPnlDrilldown
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        line={drilldown?.line}
        month={drilldown?.month || 1}
        data={data}
        year={year}
      />
    </div>
  );
}
